import React from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  BrainCircuit,
  Database,
  Layers3,
  LifeBuoy,
  Workflow,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const databricksServices = [
  {
    id: 1,
    number: "01",
    title: "Databricks Consulting & Implementation",
    description:
      "Build a secure, scalable Databricks foundation with a clear architecture, governed data, and business-focused implementation roadmap.",
    label: "Strategy & setup",
    link: "/data-ai-databricks-solutions/databricks-consulting",
    image: "/assets/services/img1.png",
    icon: Layers3,
  },
  {
    id: 2,
    number: "02",
    title: "Lakehouse Architecture & Migration",
    description:
      "Modernize legacy data platforms and bring analytics, data engineering, and AI workloads together on an open lakehouse architecture.",
    label: "Modernize your data",
    link: "/data-ai-databricks-solutions/lakehouse-architecture",
    image: "/assets/services/img2.png",
    icon: Database,
  },
  {
    id: 3,
    number: "03",
    title: "Data Engineering & Governance",
    description:
      "Create dependable data pipelines with quality checks, lineage, access controls, and governance built into everyday workflows.",
    label: "Trusted data pipelines",
    link: "/data-ai-databricks-solutions/data-engineering",
    image: "/assets/services/img3.png",
    icon: Workflow,
  },
  {
    id: 4,
    number: "04",
    title: "Generative AI & AI Agents",
    description:
      "Turn enterprise data into useful AI assistants and agents with retrieval-augmented generation, evaluation, and responsible guardrails.",
    label: "AI built for production",
    link: "/data-ai-databricks-solutions/generative-ai-agent-bricks",
    image: "/assets/services/img4.png",
    icon: BrainCircuit,
  },
  {
    id: 5,
    number: "05",
    title: "Analytics, BI & Dashboards",
    description:
      "Give every team a consistent view of performance with Databricks SQL, KPI dashboards, Power BI, Tableau, and conversational analytics.",
    label: "Decisions with clarity",
    link: "/data-ai-databricks-solutions/databricks-sql-bi",
    image: "/assets/services/img5.png",
    icon: BarChart3,
  },
  {
    id: 6,
    number: "06",
    title: "Databricks Managed Services",
    description:
      "Keep your data and AI platform reliable through proactive monitoring, platform support, operational improvements, and FinOps.",
    label: "Operate & optimize",
    link: "/data-ai-databricks-solutions/databricks-managed-services",
    image: "/assets/services/img6.png",
    icon: LifeBuoy,
  },
];

const serviceStats = [
  { value: "Data", label: "platform strategy" },
  { value: "AI", label: "ready foundations" },
  { value: "BI", label: "trusted insights" },
  { value: "Ops", label: "ongoing optimization" },
];

function DatabricksServices() {
  return (
    <main className="min-h-screen bg-white text-[#102A43]">
      
<Helmet>
  {/* Primary SEO */}
  <title>Databricks Services & Solutions | Mobikode</title>

  <meta
    name="description"
    content="Explore Databricks consulting, lakehouse migration, data engineering, GenAI agents, BI dashboards, and managed services with Mobikode."
  />

  <meta
    name="keywords"
    content="Databricks services, Databricks consulting, Databricks implementation, Databricks migration, data engineering services, Databricks AI agents, Databricks SQL, Databricks managed services"
  />

  <link
    rel="canonical"
    href="https://www.mobikode.com/data-ai-databricks-solutions/"
  />

  {/* Open Graph */}
  <meta
    property="og:title"
    content="Databricks Services & Solutions | Mobikode"
  />

  <meta
    property="og:description"
    content="Modernize your data platform with Databricks consulting, lakehouse architecture, data engineering, enterprise AI, analytics, and managed services."
  />

  <meta
    property="og:url"
    content="https://www.mobikode.com/data-ai-databricks-solutions/"
  />

  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="Mobikode" />

  <meta
    property="og:image"
    content="https://www.mobikode.com/assets/services/bg3.png"
  />

  {/* Twitter / X */}
  <meta name="twitter:card" content="summary_large_image" />

  <meta
    name="twitter:title"
    content="Databricks Services & Solutions | Mobikode"
  />

  <meta
    name="twitter:description"
    content="Build a trusted data and AI ecosystem with Mobikode's Databricks services, from consulting and migration to analytics and ongoing support."
  />

  <meta
    name="twitter:image"
    content="https://www.mobikode.com/assets/services/bg3.png"
  />
</Helmet>

  <section className="relative w-full h-[500px] lg:h-[630px] flex items-center bg-[#000814] overflow-hidden py-12 md:py-0">
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/cloud/bg1.jpeg"
            alt="Abstract 3D Background"
            className="w-full h-full object-cover"
          />
          <div
            style={{ background: "linear-gradient(86.84deg, #000000 20%, rgba(0, 0, 0, 0) 79%)" }}
            className="hidden md:block absolute inset-0"
          ></div>
          <div
            style={{ background: "linear-gradient(0deg, #000000 40%, rgba(0, 0, 0, 0.3) 100%)" }}
            className="block md:hidden absolute inset-0"
          ></div>
        </div>
        <div className="relative z-10 w-full max-w-[1440px] mx-auto px-[20px] md:px-[40px] lg:px-[80px] flex flex-col items-start justify-center h-full">
          <div className="w-full max-w-[500px] md:max-w-[700px] lg:max-w-[900px] mt-auto md:mt-0">
            <h1 className="text-white font-[600] text-[28px] sm:text-[36px] md:text-[50px] lg:text-[60px] font-sora leading-[1.2] lg:leading-[1.1] mb-[12px] lg:mb-[22px] drop-shadow-xl tracking-wide text-left">
            Databricks Partner · Data & AI Solutions
            </h1>
            <p className="text-[#FFFFFF] max-w-[800px] w-full text-[14px] sm:text-[16px] md:text-[18px] leading-[20px] md:leading-[22px] lg:leading-[24px] font-graphik font-light tracking-wider text-left opacity-90 lg:opacity-100">
                From Databricks architecture and governed data pipelines to AI agents,
              business intelligence, and managed operations — connect the right
              capabilities to move from data complexity to measurable outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section id="databricks-services" className="bg-white py-16 sm:py-20 lg:py-24">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-20">
          <div className="mb-10 flex flex-col gap-5 md:mb-14 md:flex-row md:items-end md:justify-between">
            <div className="max-w-[760px]">
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#ED6800] sm:text-sm">What we do</p>
              <h2 className="font-sora text-3xl font-semibold leading-tight text-[#01386A] sm:text-4xl lg:text-[46px]">
                The right expertise for every stage of your data journey.
              </h2>
            </div>
            <p className="max-w-[430px] text-sm leading-7 text-slate-500 sm:text-base">
              Choose a service to explore how we can help your team design,
              build, secure, and operate a modern data and AI platform.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {databricksServices.map((service, index) => {
              const ServiceIcon = service.icon;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.12 }}
                  transition={{ duration: 0.45, delay: (index % 3) * 0.07 }}
                  className="h-full"
                >
                  <Link
                    to={service.link}
                    className="group relative flex h-full min-h-[320px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_6px_24px_rgba(15,23,42,0.035)] transition duration-300 hover:-translate-y-1.5 hover:border-[#01386A]/25 hover:shadow-[0_20px_50px_rgba(1,56,106,0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ED6800] focus-visible:ring-offset-2 sm:p-7"
                  >
                    <div className="pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full bg-[#F3F7FB] transition duration-500 group-hover:scale-125 group-hover:bg-[#FFF1E7]" />
                    <div className="relative flex items-start justify-between gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#EDF4FA] text-[#01386A] transition-colors duration-300 group-hover:bg-[#01386A] group-hover:text-white">
                        <ServiceIcon size={23} strokeWidth={1.8} />
                      </div>
                     
                    </div>

                    <div className="relative mt-7 flex-1">
                      <span className="inline-flex rounded-full bg-[#FFF4EB] px-3 py-1 text-[11px] font-semibold tracking-wide text-[#C65300]">
                        {service.label}
                      </span>
                      <h3 className="mt-4 max-w-[340px] font-sora text-xl font-semibold leading-snug text-[#01386A] transition-colors group-hover:text-[#ED6800] sm:text-[22px]">
                        {service.title}
                      </h3>
                      <p className="mt-3 text-sm leading-7 text-slate-600">{service.description}</p>
                    </div>

                    <div className="relative mt-7 flex items-center justify-between border-t border-slate-100 pt-5">
                      <span className="font-sora text-sm font-semibold text-[#01386A]">Explore service</span>
                      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-[#ED6800] transition duration-300 group-hover:border-[#ED6800] group-hover:bg-[#ED6800] group-hover:text-white">
                        <ArrowUpRight size={18} />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="px-5 pb-16 sm:px-8 sm:pb-20 lg:px-20 lg:pb-24">
        <div className="relative mx-auto max-w-[1280px] overflow-hidden rounded-2xl bg-[#01386A] px-6 py-10 sm:px-10 sm:py-12 lg:px-14 lg:py-14">
          <div className="absolute -right-12 -top-24 h-64 w-64 rounded-full border-[36px] border-white/5" aria-hidden="true" />
          <div className="absolute -bottom-24 right-40 h-48 w-48 rounded-full bg-[#ED6800]/20 blur-3xl" aria-hidden="true" />
          <div className="relative flex flex-col gap-7 md:flex-row md:items-center md:justify-between">
            <div className="max-w-[760px]">
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#FFB27A] sm:text-sm">Start with a clear plan</p>
              <h2 className="font-sora text-3xl font-semibold leading-tight text-white sm:text-4xl">
                Ready to get more from your data and AI investments?
              </h2>
              <p className="mt-4 max-w-[680px] text-sm leading-7 text-white/75 sm:text-base">
                Let’s identify the right starting point and build a practical roadmap around your business goals.
              </p>
            </div>
            <Link
              to="/data-ai-databricks-solutions/databricks-consulting"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-[#ED6800] px-6 py-3.5 font-sora text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-[#d95e00]"
            >
              Talk to our team <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

export default DatabricksServices;
